import type { CSSProperties, HTMLAttributes, PropsWithChildren } from 'react';
import { forwardRef } from 'react';
import styled from 'styled-components';
import type { BuilderContentAlignment } from '../../Builder';
import { useBanner } from '../../bannerProvider';

export type BlockProps = HTMLAttributes<HTMLDivElement> &
  PropsWithChildren<{
    alignment?: BuilderContentAlignment;
    minWidth?: string | number;
    minHeight?: string | number;
  }>;

export const Block = forwardRef<HTMLDivElement, BlockProps>(
  (
    {
      children,
      className,
      alignment = 'center',
      minWidth = 'auto',
      minHeight = 'auto',
      style,
      onClick
    },
    forwardingRef
  ) => {
    const { contentSpacing, contentDirection } = useBanner();

    const isColumn = contentDirection === 'column';
    const marginNameStart = isColumn ? 'top' : 'left';
    const marginNameEnd = isColumn ? 'bottom' : 'right';

    const baseStyles = {
      minWidth,
      minHeight,
      textAlign: alignment.replace(/flex-/g, ''),
      justifyContent: alignment
    } as CSSProperties;

    return (
      <BaseBlock
        _marginNameStart={marginNameStart}
        _marginNameEnd={marginNameEnd}
        _spacing={contentSpacing}
        _isColumn={isColumn}
        className={className}
        ref={forwardingRef}
        style={{
          ...baseStyles,
          ...style
        }}
        onClick={onClick}
      >
        {children}
      </BaseBlock>
    );
  }
);

export const BaseBlock = styled.div<{
  _marginNameStart: 'top' | 'left';
  _marginNameEnd: 'bottom' | 'right';
  _spacing: number;
  _isColumn: boolean;
}>`
  ${({ _marginNameStart, _spacing, _isColumn }) => `
    display: flex;
    position: relative;
    ${_isColumn ? `width: 100%;` : `height: 100%;`}
    margin: ${_isColumn ? `${_spacing}px 0 0 0;` : `0 0 0 ${_spacing}px;`}


    &:first-child {
      margin-${_marginNameStart}: 0;
    }
  `}
`;
